// Mock / simulated content for the Conduit outbound landing page.
// The interactive demo uses illustrative numbers only.

export const CALENDLY_URL = "https://calendly.com/mvinayviswanadh2024/";
export const LINKEDIN_PERSONAL = "https://www.linkedin.com/in/vinay-viswanadh/";
export const LINKEDIN_COMPANY = "https://www.linkedin.com/company/conduitoutbound/";

export const OFFICE_IMAGE =
  "https://images.unsplash.com/photo-1560264280-88b68371db39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzkwMzI0ODQ2fDA&ixlib=rb-4.1.0&q=85";

export const HERO_STATS = [
  { value: "41M+ contacts", label: "B2B database across the UK, Europe and the US" },
  { value: "3-layer verification", label: "SMTP handshake, pattern match and human QA" },
  { value: "Under 3% bounce", label: "Every list checked before it is sent" },
];

export const CHANNELS = [
  { name: "Google Workspace", short: "GW" },
  { name: "Microsoft 365", short: "M365" },
  { name: "LinkedIn", short: "in" },
  { name: "Coresignal", short: "CS" },
  { name: "YC directory", short: "YC" },
];

export const REPLY_FACTS = [
  { k: "Live now", v: "3 client campaigns running" },
  { k: "Markets", v: "8 countries across Europe and the US" },
  { k: "Billing", v: "Monthly, in advance" },
];

export const MARKETS = [
  { name: "United States", tag: "Campaigns live" },
  { name: "United Kingdom", tag: "Clients based here" },
  { name: "Ireland", tag: "Campaigns live" },
  { name: "Netherlands", tag: "Campaigns live" },
  { name: "Belgium", tag: "Campaigns live" },
  { name: "Norway", tag: "Campaigns live" },
  { name: "Sweden", tag: "Campaigns live" },
];

export const STEPS = [
  {
    n: "01",
    when: "Day 1",
    title: "Brief and ideal customer.",
    body:
      "One call to agree industries, company size, titles and regions. You approve it in writing before anything is sent.",
  },
  {
    n: "02",
    when: "Weeks 1 to 2",
    title: "Infrastructure and lists.",
    body:
      "Separate domains and inboxes, warmed before launch, so your main domain is never touched. Meanwhile we build lists of accounts with a reason to buy now, verified with direct dials.",
  },
  {
    n: "03",
    when: "Week 3",
    title: "Sequences under your name.",
    body:
      "Short, specific emails sent as your account executive, with LinkedIn touches alongside. You approve every sequence.",
  },
  {
    n: "04",
    when: "Ongoing",
    title: "Replies to your team.",
    body:
      "Interested replies reach you the same day with context. Out-of-office and not-now replies are handled for you. One written report a month.",
  },
];

export const DEMO_SCENARIOS = [
  {
    id: "nordics",
    label: "Construction SaaS to Nordics",
    lines: [
      "$ conduit build --market=nordics --icp=construction-saas",
      "› resolving signals ......... 12,480 accounts matched",
      "› filtering: turnover > \u00a350M ... 3,120 accounts",
      "› verifying emails (3-layer) ... bounce risk 2.1%",
      "› warming 6 inboxes ......... ready",
      "› drafting sequence (4 steps) . approved",
    ],
    results: [
      { k: "Accounts targeted", v: "3,120" },
      { k: "Emails / day", v: "~180" },
      { k: "Projected replies / mo", v: "41" },
      { k: "Meetings booked / mo", v: "9" },
    ],
  },
  {
    id: "us-saas",
    label: "Agency to US SaaS",
    lines: [
      "$ conduit build --market=us --icp=b2b-saas",
      "› resolving signals ......... 28,900 accounts matched",
      "› filtering: 100-500 staff ... 6,740 accounts",
      "› verifying emails (3-layer) ... bounce risk 2.6%",
      "› warming 12 inboxes ........ ready",
      "› drafting sequence (4 steps) . approved",
    ],
    results: [
      { k: "Accounts targeted", v: "6,740" },
      { k: "Emails / day", v: "~360" },
      { k: "Projected replies / mo", v: "73" },
      { k: "Meetings booked / mo", v: "14" },
    ],
  },
  {
    id: "uk-erp",
    label: "Trades ERP to UK",
    lines: [
      "$ conduit build --market=uk --icp=electrical-contractors",
      "› resolving signals ......... 9,210 accounts matched",
      "› filtering: 10-30 staff .... 4,050 accounts",
      "› verifying emails (3-layer) ... bounce risk 1.8%",
      "› warming 6 inboxes ......... ready",
      "› drafting sequence (4 steps) . approved",
    ],
    results: [
      { k: "Accounts targeted", v: "4,050" },
      { k: "Emails / day", v: "~180" },
      { k: "Projected replies / mo", v: "52" },
      { k: "Meetings booked / mo", v: "11" },
    ],
  },
];

export const CLIENTS = [
  {
    mono: "CS",
    name: "Construction software",
    place: "Helsinki, Finland",
    status: "Live",
    a: "7 European markets",
    b: "\u00a350M+ target turnover floor",
    body:
      "Outbound to project managers at large contractors, data-centre projects first. Lists handed to the client's own SDR team.",
  },
  {
    mono: "WA",
    name: "Webflow agency",
    place: "West Yorkshire, UK",
    status: "Live",
    a: "US target market",
    b: "100\u2013500 staff per account",
    body:
      "Outbound to US-headquartered B2B SaaS and tech companies, leading with website migration.",
  },
  {
    mono: "TE",
    name: "Trades ERP",
    place: "Finland",
    status: "Onboarding",
    a: "10\u201320 UK pilot customers",
    b: "10\u201330 staff per contractor",
    body:
      "Outbound to UK electrical contractors still running jobs on spreadsheets and disconnected tools.",
  },
  {
    mono: "RA",
    name: "Recruitment agencies",
    place: "United Kingdom",
    status: "Accepting",
    a: "BD new client development",
    b: "5\u201350 consultants per agency",
    body:
      "Outbound to hiring managers at companies with open roles in the agency's specialism, timed to fresh job posts so the first email lands while the need is live.",
  },
  {
    mono: "SA",
    name: "SaaS companies",
    place: "Europe and India",
    status: "Accepting",
    a: "$12K+ typical annual contract",
    b: "1\u20133 new markets at once",
    body:
      "Outbound for B2B software entering a market without a local sales team yet, with lists and copy built per country and replies routed to whoever closes.",
  },
];

export const CURRENCIES = {
  EUR: { symbol: "\u20ac", rates: [1600, 2560, 3430] },
  GBP: { symbol: "\u00a3", rates: [1400, 2240, 3000] },
  USD: { symbol: "$", rates: [1750, 2800, 3750] },
  INR: { symbol: "\u20b9", rates: [145000, 232000, 311000] },
};

export const PLANS = [
  {
    name: "Single market",
    tag: null,
    minimum: "3-month minimum",
    features: [
      "3 domains, 6 inboxes, around 180 emails a day",
      "Signal-based lists with phone numbers",
      "One market, one sequence set",
    ],
    footer: "Testing one new market",
  },
  {
    name: "Two markets",
    tag: "Most chosen",
    minimum: "3-month minimum",
    features: [
      "6 domains, 12 inboxes",
      "Two markets in parallel, e.g. UK and US",
      "Separate sequences per market",
    ],
    footer: "Scaling what already works",
  },
  {
    name: "Managed replies",
    tag: null,
    minimum: "3-month minimum",
    features: [
      "Everything in Two markets",
      "We qualify every reply",
      "Meetings booked into your calendar",
    ],
    footer: "No SDR team in-market yet",
  },
];

export const FORM_OPTIONS = {
  sell: ["B2B software", "Agency or services", "Recruitment", "Other B2B", "Consumer (B2C)"],
  deal: ["Under $2,300", "$2,300 to $12,000", "$12,000 to $59,000", "$59,000+"],
  markets: ["UK", "Ireland", "Nordics", "Benelux", "Rest of Europe", "US"],
  budget: ["Under $1,200", "$1,200 to $1,749", "$1,750 to $2,900", "$2,900 to $5,900", "$5,900+"],
  meetings: ["A sales team or SDRs", "The founder", "Nobody yet"],
  start: ["Within 2 weeks", "This month", "Next quarter", "Just researching"],
};
